/**
 * modules/Overview.jsx
 * Platform overview / home dashboard
 */

import { useState, useEffect } from "react";
import { api, Spinner } from "../utils/api";

export function Overview() {
  const [stats, setStats]       = useState(null);
  const [intel, setIntel]       = useState([]);
  const [briefs, setBriefs]     = useState([]);
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/stats').catch(() => ({})),
      api.get('/api/intel/feed?limit=5&min_score=60').catch(() => []),
      api.get('/api/briefs?status=Active').catch(() => []),
      api.get('/api/ats/pipeline').catch(() => ({})),
    ]).then(([s, i, b, p]) => {
      setStats(s); setIntel(i || []); setBriefs((b || []).slice(0,4)); setPipeline(p || {});
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{padding:60, display:'flex', justifyContent:'center'}}><Spinner/></div>;

  const stages = ['Sourced','Screened','Interviewing','Offer','Placed'];
  const totalCandidates = stages.reduce((acc,s) => acc + (pipeline[s] || 0), 0);
  const today = new Date();
  const greeting = today.getHours() < 12 ? 'Good morning' : today.getHours() < 18 ? 'Good afternoon' : 'Good evening';
  const dateStr = today.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' });

  const userName = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('hyring_user') || '{}');
      return (u.name || '').split(' ')[0] || 'there';
    } catch { return 'there'; }
  })();

  const isEmpty = totalCandidates === 0 && intel.length === 0 && briefs.length === 0;

  const statCards = [
    { label:'Jobs scraped today',  val: stats?.jobs_today       ?? 0, cls:'or', icon:'⊕' },
    { label:'Active candidates',   val: stats?.total_candidates ?? 0, cls:'gd', icon:'◫' },
    { label:'Open client briefs',  val: stats?.active_briefs    ?? 0, cls:'tl', icon:'◎' },
    { label:'Intel signals today', val: stats?.signals_today    ?? 0, cls:'bl', icon:'⚡' },
  ];

  return (
    <div style={{display:'flex', flexDirection:'column', gap:24}}>

      {/* HERO — gradient panel for the greeting */}
      <div className="animate-in" style={{
        position:'relative',
        background:'linear-gradient(135deg, rgba(255,112,9,0.10) 0%, rgba(255,6,99,0.08) 100%)',
        border:'1px solid rgba(255,92,26,0.2)',
        borderRadius:14,
        padding:'24px 28px',
        overflow:'hidden',
      }}>
        <div style={{position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%', background:'radial-gradient(circle, rgba(255,112,9,0.15) 0%, transparent 70%)', pointerEvents:'none'}}/>
        <div style={{fontSize:26, fontWeight:700, letterSpacing:'-0.02em', color:'#fff', marginBottom:6}}>
          {greeting}, {userName}
        </div>
        <div style={{fontSize:12, color:'#bbb', fontFamily:'var(--mono)', letterSpacing:'0.03em'}}>
          {dateStr.toUpperCase()} · {isEmpty ? 'WELCOME TO HYRING' : intel.length > 0 ? `${intel.length} NEW SIGNALS OVERNIGHT` : 'ALL CAUGHT UP'}
        </div>
      </div>

      {/* STATS */}
      <div className="stats-grid">
        {statCards.map((s,i) => (
          <div key={i} className={`stat ${s.cls} animate-in`} style={{animationDelay:`${i*0.05}s`, transition:'transform 0.15s ease, border-color 0.15s'}}
               onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.borderColor='rgba(255,255,255,0.12)';}}
               onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.borderColor='var(--b)';}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
              <div className="stat-val">{s.val}</div>
              <div style={{fontSize:18, opacity:0.5}}>{s.icon}</div>
            </div>
            <div className="stat-lbl" style={{color:'#aaa'}}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* QUICK START — only shows when everything is empty */}
      {isEmpty && (
        <div className="card animate-in" style={{animationDelay:'0.15s', background:'linear-gradient(135deg, rgba(255,112,9,0.04) 0%, rgba(255,6,99,0.04) 100%)', border:'1px solid rgba(255,92,26,0.15)'}}>
          <div className="panel-body" style={{padding:'22px 24px'}}>
            <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:14}}>
              <div style={{width:30, height:30, borderRadius:8, background:'linear-gradient(135deg, var(--or-start), var(--or-end))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14}}>✨</div>
              <div style={{fontSize:14, fontWeight:600, color:'#fff'}}>Quick start</div>
            </div>
            <div style={{fontSize:13, color:'#bbb', marginBottom:18, lineHeight:1.6}}>
              Your platform is ready. Start by setting up your first job brief, adding candidates, or letting the AI scrape MENA market intel.
            </div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10}}>
              {[
                {icon:'◎', label:'Create your first brief', sub:'Define a role for AI matching'},
                {icon:'◫', label:'Add candidates', sub:'Build your talent pipeline'},
                {icon:'⚡', label:'Run market intel scan', sub:'Surface MENA hiring signals'},
              ].map((q,i) => (
                <div key={i} style={{padding:14, background:'var(--card2)', border:'1px solid var(--b)', borderRadius:10, cursor:'pointer', transition:'all 0.15s'}}
                     onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(255,92,26,0.3)'; e.currentTarget.style.background='rgba(255,92,26,0.04)';}}
                     onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--b)'; e.currentTarget.style.background='var(--card2)';}}>
                  <div style={{fontSize:18, marginBottom:8}}>{q.icon}</div>
                  <div style={{fontSize:12, fontWeight:600, color:'#fff', marginBottom:3}}>{q.label}</div>
                  <div style={{fontSize:10, color:'#999', fontFamily:'var(--mono)', lineHeight:1.5}}>{q.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* MAIN CONTENT — two columns */}
      <div className="two-col">

        {/* INTEL FEED */}
        <div className="card animate-in" style={{animationDelay:'0.2s'}}>
          <div className="panel-hdr">
            <span className="panel-title" style={{color:'#fff'}}>Latest intelligence</span>
            <span style={{fontFamily:'var(--mono)', fontSize:11, color:'var(--or)', cursor:'pointer'}}>VIEW ALL →</span>
          </div>
          <div className="panel-body" style={{paddingTop:8}}>
            {intel.length === 0 ? (
              <EmptyBlock icon="⚡" title="No signals yet" message="Intel scrape runs nightly at 02:30 UTC."/>
            ) : intel.map(item => (
              <div key={item.id} style={{display:'flex', gap:12, padding:'12px 0', borderBottom:'1px solid var(--b)'}}>
                <div style={{width:36, height:36, borderRadius:9, background:'linear-gradient(135deg, rgba(255,112,9,0.2), rgba(255,6,99,0.15))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0}}>⚡</div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:600, color:'#fff', marginBottom:6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{item.title}</div>
                  <div style={{display:'flex', gap:10, alignItems:'center'}}>
                    <span style={{fontFamily:'var(--mono)', fontSize:10, color:'#999'}}>{item.time_ago || 'recent'}</span>
                    <div style={{flex:1, height:3, background:'rgba(255,255,255,0.06)', borderRadius:2, overflow:'hidden'}}>
                      <div style={{width:`${item.score || 0}%`, height:'100%', background: scoreColor(item.score || 0), borderRadius:2}}/>
                    </div>
                    <span style={{fontFamily:'var(--mono)', fontSize:10, color: scoreColor(item.score || 0), minWidth:20, textAlign:'right'}}>{item.score || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* PIPELINE + BRIEFS */}
        <div style={{display:'flex', flexDirection:'column', gap:14}}>

          <div className="card animate-in" style={{animationDelay:'0.25s'}}>
            <div className="panel-hdr">
              <span className="panel-title" style={{color:'#fff'}}>Candidate pipeline</span>
              <span style={{fontFamily:'var(--mono)', fontSize:11, color:'#999'}}>{totalCandidates} TOTAL</span>
            </div>
            <div className="panel-body">
              <div style={{display:'flex', flexDirection:'column', gap:12}}>
                {stages.map(stage => {
                  const count = pipeline[stage] || 0;
                  const pct = totalCandidates ? (count / totalCandidates) * 100 : 0;
                  const isHot = stage === 'Offer' || stage === 'Placed';
                  return (
                    <div key={stage}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6}}>
                        <span style={{fontFamily:'var(--mono)', fontSize:10, color:'#aaa', textTransform:'uppercase', letterSpacing:'0.1em', fontWeight:500}}>{stage}</span>
                        <span style={{fontSize:14, fontWeight:700, color: isHot ? 'var(--gd)' : '#fff'}}>{count}</span>
                      </div>
                      <div style={{height:5, background:'rgba(255,255,255,0.05)', borderRadius:3, overflow:'hidden'}}>
                        <div style={{width:`${Math.max(pct, count?5:0)}%`, height:'100%', background: isHot ? 'linear-gradient(90deg, var(--or-start), var(--or-end))' : 'rgba(255,92,26,0.5)', borderRadius:3, transition:'width 0.5s'}}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="card animate-in" style={{animationDelay:'0.3s'}}>
            <div className="panel-hdr">
              <span className="panel-title" style={{color:'#fff'}}>Active briefs</span>
              <span style={{fontFamily:'var(--mono)', fontSize:11, color:'var(--or)', cursor:'pointer'}}>VIEW ALL →</span>
            </div>
            <div className="panel-body" style={{paddingTop:8}}>
              {briefs.length === 0 ? (
                <EmptyBlock icon="◎" title="No active briefs" message="Create a brief to start AI-matching candidates."/>
              ) : briefs.map(b => (
                <div key={b.id} style={{display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid var(--b)'}}>
                  <div style={{width:34, height:34, borderRadius:9, background:'linear-gradient(135deg, var(--or-start), var(--or-end))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0}}>
                    {(b.company_name || '?').slice(0,2).toUpperCase()}
                  </div>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:13, fontWeight:600, color:'#fff', marginBottom:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{b.title}</div>
                    <div style={{fontSize:11, color:'#999', fontFamily:'var(--mono)'}}>{b.company_name}{b.location ? ` · ${b.location}` : ''}</div>
                  </div>
                  <span className="badge or">{b.status || 'Active'}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

function scoreColor(score) {
  return score >= 85 ? '#4ade80' : score >= 70 ? 'var(--gd)' : score >= 50 ? 'var(--or)' : 'var(--mu)';
}

function EmptyBlock({ icon, title, message }) {
  return (
    <div style={{textAlign:'center', padding:'36px 20px', color:'#888', fontFamily:'var(--mono)'}}>
      <div style={{width:48, height:48, borderRadius:12, background:'linear-gradient(135deg, rgba(255,112,9,0.1), rgba(255,6,99,0.05))', border:'1px solid rgba(255,92,26,0.2)', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:20, marginBottom:12}}>{icon}</div>
      <div style={{fontSize:13, color:'#ccc', marginBottom:4, fontFamily:'var(--sans)', fontWeight:600}}>{title}</div>
      <div style={{fontSize:11, color:'#777'}}>{message}</div>
    </div>
  );
}
